/**
 * ORT-3057 add-to-cart island: data contract between Twig and Preact.
 *
 * Twig marks one *root* element per brick instance (`data-add-to-cart-root`) carrying its product id
 * (`data-add-to-cart-product="<id>"`). Inside the root, *slots* (`data-add-to-cart-slot`) are the holes
 * Preact renders into. Everything else in the brick stays Twig.
 *
 * Data is page-level: layout.html.twig emits one `page-product-variants-json` (PIM: product, dosage
 * forms, buyable pack sizes) for all products with an island on the page, built by PageProductVariantsCollector
 * in one pass. So 50 cards (ORT-3055) cost one JSON, not 50 sub-requests.
 *
 * The JSON is deliberately slim (DosageFormsPayloadBuilder::buildAddToCartVariants): only pack sizes that can
 * go into the cart, and only the fields below. Everything else (Shopware ids, stock, prices) comes from
 * /pdp-data at click time; the PDP keeps its own full product-dosageforms-json.
 */
import type { DosageForm, Packsize } from '../types';
import type { PageONumberPriceEntry, PageProductPriceEntry, PageProductPrices } from '../JsonProvider';

export const ROOT_SELECTOR = '[data-add-to-cart-root]';
export const slotSelector = (name: 'price' | 'button') => `[data-add-to-cart-slot="${name}"]`;

export interface AddToCartProduct {
  id: number;
  name: string;
  /** HTML from the PIM, sanitize before rendering */
  description: string;
  /** PDP url, used as graceful fallback when the shop is unavailable */
  url: string;
}

/** Slim pack size: the PDP's Packsize reduced to what the island reads (keep in sync with the PHP builder) */
export type AddToCartPacksize = Pick<Packsize, 'oNumber' | 'size' | 'packshotUrl' | 'packsizeVisualUrl' | 'hideBasePrice'>;

/** Slim dosage form: name (selector, GA4 item_variant), productName (GA4 item_name) and its buyable pack sizes */
export interface AddToCartDosageForm extends Pick<DosageForm, 'name' | 'productName'> {
  packsizes: AddToCartPacksize[];
}

export interface AddToCartPayload {
  product: AddToCartProduct;
  /** Dosage forms with at least one buyable pack size; [] when nothing is buyable (island -> fallback link) */
  dosageForms: AddToCartDosageForm[];
}

/** One buyable pack size, flattened out of the dosage form tree */
export interface Variant {
  dosageForm: AddToCartDosageForm;
  packsize: AddToCartPacksize;
}

export const PAGE_VARIANTS_SCRIPT_ID = 'page-product-variants-json';

type PageVariants = Record<string, AddToCartPayload>;
// Parsed once per page; `undefined` = not read yet, `null` = missing or broken
let pageVariantsCache: PageVariants | null | undefined;

function readPageVariants(): PageVariants | null {
  if (pageVariantsCache !== undefined) return pageVariantsCache;
  const script = document.getElementById(PAGE_VARIANTS_SCRIPT_ID);
  if (!script) {
    console.error('[eshop] [add-to-cart] page variants JSON not found (#' + PAGE_VARIANTS_SCRIPT_ID + ')');
    pageVariantsCache = null;
    return null;
  }
  try {
    pageVariantsCache = JSON.parse(script.textContent || '') as PageVariants;
  } catch (err) {
    console.error('[eshop] [add-to-cart] page variants JSON parse error', err);
    pageVariantsCache = null;
  }
  return pageVariantsCache;
}

/** The island's data: the root's product looked up in the page-level variants JSON. */
export function readAddToCartPayload(root: HTMLElement): AddToCartPayload | null {
  const productId = root.dataset.addToCartProduct;
  if (!productId) {
    console.error('[eshop] [add-to-cart] root without data-add-to-cart-product', root);
    return null;
  }
  const entry = readPageVariants()?.[productId];
  if (!entry?.product || !Array.isArray(entry?.dosageForms)) {
    console.error('[eshop] [add-to-cart] no variants entry for product', productId);
    return null;
  }
  return entry;
}

/**
 * Flattens the dosage form tree into selectable variants. The server already dropped shop-disabled and
 * pharmacy-only pack sizes; the o-number guard stays as a cheap safety net.
 */
export function getBuyableVariants(dosageForms: AddToCartDosageForm[]): Variant[] {
  const variants: Variant[] = [];
  for (const dosageForm of dosageForms ?? []) {
    for (const packsize of dosageForm.packsizes ?? []) {
      if (!packsize.oNumber) continue;
      variants.push({ dosageForm, packsize });
    }
  }
  return variants;
}

/**
 * Dev/test toggle, analogous to `?country=at` in pdpData: `?atc=loading` keeps every add-to-cart
 * island in its loading state (no /pdp-data request), so the skeleton presentation can be inspected.
 */
export function isLoadingHoldFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('atc') === 'loading';
}

/* ------------------------------------------------------------------ *
 * Price source per island (ORT-3055 vs ORT-3057)
 * ------------------------------------------------------------------ */

/**
 * `data-add-to-cart-prices="snapshot"` on a root: first paint from the page-level snapshot JSON
 * (PR #1509, no Shopware call on page load; for pages with many products such as the overview).
 * Default `live`: one /pdp-data call on mount for this product's pack sizes (hero, single product).
 * In both modes the modal / direct add fetch /pdp-data live for the Shopware ids and stock.
 */
export type PriceMode = 'live' | 'snapshot';

export function getPriceMode(root: HTMLElement): PriceMode {
  return root.dataset.addToCartPrices === 'snapshot' ? 'snapshot' : 'live';
}

/**
 * This product's aggregate from page-product-prices-json for the effective country
 * (?country=at test toggle, else the page's default). null = no usable snapshot -> fallback.
 */
export function readSnapshotEntry(
  prices: PageProductPrices | null,
  productId: string,
  countryOverride: string | null
): PageProductPriceEntry | null {
  if (!prices) return null;
  const country = countryOverride ?? prices.defaultCountry;
  return prices.countries?.[country]?.products?.[productId] ?? null;
}

/**
 * ORT-3175: the brick's pack size preselection (`data-add-to-cart-onumber`), resolved against the buyable
 * variants. null without a preselection or when it is stale (not buyable for this locale): the island
 * then behaves as without one ("ab" price).
 */
export function getPreselectedVariant(root: HTMLElement, variants: Variant[]): Variant | null {
  const oNumber = root.dataset.addToCartOnumber;
  if (!oNumber) return null;
  return variants.find((v) => v.packsize.oNumber === oNumber) ?? null;
}

/** One pack size's snapshot entry from page-product-prices-json, see readSnapshotEntry(). */
export function readSnapshotONumberEntry(
  prices: PageProductPrices | null,
  oNumber: string,
  countryOverride: string | null
): PageONumberPriceEntry | null {
  if (!prices) return null;
  const country = countryOverride ?? prices.defaultCountry;
  return prices.countries?.[country]?.onumbers?.[oNumber] ?? null;
}
